import React, { memo } from 'react'
import { Copy, Edit, Link2, MoreVertical, Play, Trash2, Unlink } from 'lucide-react'
import { LabelChips } from '@/components/labels/LabelChips'
import { SelectButton } from '@/components/ui/SelectButton'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { PLAY_TYPE_PASS } from './constants/Playbook'
import { CanvasThumbnail } from '@/components/canvas-renderer'
import type { Drawing } from '@/types/Drawing.types'
import type { Player } from '@/types/Player.types'
import './play-card.css'

/** Efficient shallow comparison for Player arrays - avoids JSON.stringify overhead */
function playersEqual(a?: Player[], b?: Player[]): boolean {
	if (a === b) return true
	if (!a || !b || a.length !== b.length) return false
	for (let i = 0; i < a.length; i++) {
		const pa = a[i]
		const pb = b[i]
		if (!pa || !pb) return false
		if (pa.id !== pb.id || pa.x !== pb.x || pa.y !== pb.y ||
			pa.label !== pb.label || pa.color !== pb.color || pa.isLineman !== pb.isLineman) {
			return false
		}
	}
	return true
}

/** Efficient shallow comparison for Drawing arrays */
function drawingsEqual(a?: Drawing[], b?: Drawing[]): boolean {
	if (a === b) return true
	if (!a || !b || a.length !== b.length) return false
	// Drawings are complex objects with nested points - use JSON for correctness
	// but short-circuit on length difference first (already done above)
	return JSON.stringify(a) === JSON.stringify(b)
}

type PlayCardProps = {
	id: string
	name: string
	// Play-specific metadata (optional for formations/concepts)
	formation?: string
	playType?: string
	defensiveFormation?: string
	lastModified?: string
	thumbnail?: string
	drawings?: Drawing[]
	players?: Player[]
	personnel?: string
	labels?: string[]
	labelObjects?: { id: string; name: string; color: string }[]
	selected?: boolean
	onSelect?: (id: string) => void
	onOpen: (id: string) => void
	onAnimate?: (id: string) => void
	onRename: (id: string) => void
	onDelete: (id: string) => void
	onDuplicate: (id: string) => void
	// Concept-specific props
	cardType?: 'play' | 'concept' | 'formation'
	category?: string
	description?: string | null
	// Animation prop
	animateIndex?: number
	// Linked formation props (auto-mirror)
	linkedFormationId?: string
	linkedFormationName?: string
	onUnlink?: (id: string) => void
}

type PlayCardThumbnailProps = {
	id: string
	thumbnail?: string
	drawings?: Drawing[]
	players?: Player[]
	name: string
	playType?: string
	onOpen: () => void
	onAnimate?: () => void
	// Concept-specific props
	cardType?: 'play' | 'concept' | 'formation'
	category?: string
	// Linked formation indicator
	isLinked?: boolean
}

function PlayCardThumbnail({
							   id,
							   thumbnail,
							   drawings: _drawings,
							   players: _players,
							   name,
							   playType,
							   onOpen,
							   onAnimate,
							   cardType,
							   category,
							   isLinked,
						   }: PlayCardThumbnailProps) {
	// Type badge mapping for concepts
	const TYPE_BADGES = {
		concept: { label: 'Route', className: 'play-card-type-badge-route' },
		formation: { label: 'Formation', className: 'play-card-type-badge-formation' },
	} as const

	// Handle formation cards - formations are blocks, use blockId fetch like plays
	if (cardType === 'formation') {
		const typeBadge = TYPE_BADGES.formation

		return (
			<div className="play-card-thumbnail">
				<div
					onClick={onOpen}
					className="play-card-thumbnail-button"
				>
					<CanvasThumbnail
						blockId={id}
						className="play-card-icon-full"
					/>
				</div>

				{/* Linked formation indicator */}
				{isLinked && (
					<div className="play-card-linked-indicator" title="Linked mirror formation">
						<Link2 className="play-card-linked-icon" />
					</div>
				)}

				{/* Formation badges */}
				<div className="play-card-badges">
					{category && category !== 'route' && (
						<span className={`play-card-badge play-card-badge-${category}`}>
							{category.charAt(0).toUpperCase() + category.slice(1)}
						</span>
					)}
					<span className={`play-card-badge ${typeBadge.className}`}>
						{typeBadge.label}
					</span>
				</div>
			</div>
		)
	}

	// Handle other concept cards (use static thumbnail)
	if (cardType && cardType !== 'play') {
		const typeBadge = TYPE_BADGES[cardType]
		return (
			<div className="play-card-thumbnail">
				<div
					onClick={onOpen}
					className="play-card-thumbnail-button"
				>
					{thumbnail ? (
						<img src={thumbnail} alt={name} className="play-card-thumbnail-image" />
					) : (
						<span className="play-card-thumbnail-placeholder">No preview</span>
					)}
				</div>

				{/* Concept badges */}
				<div className="play-card-badges">
					{category && category !== 'route' && (
						<span className={`play-card-badge play-card-badge-${category}`}>
							{category.charAt(0).toUpperCase() + category.slice(1)}
						</span>
					)}
					<span className={`play-card-badge ${typeBadge.className}`}>
						{typeBadge.label}
					</span>
				</div>
			</div>
		)
	}

	// Handle play cards (use CanvasThumbnail)
	const badgeClass = playType == PLAY_TYPE_PASS
		? 'play-card-type-badge-pass'
		: 'play-card-type-badge-run'

	return (
		<div className="play-card-thumbnail">
			<div
				onClick={onOpen}
				className="play-card-thumbnail-button"
			>
				<CanvasThumbnail
					blockId={id}
					className="play-card-icon-full"
				/>
			</div>

			{playType && (
				<span className={`play-card-type-badge ${badgeClass}`}>
          {playType}
        </span>
			)}

			{onAnimate && (
				<button
					onClick={(e) => {
						e.stopPropagation()
						onAnimate()
					}}
					className="play-card-animate-button"
					aria-label="Animate play"
				>
					<Play className="play-card-icon-large play-card-icon-white" />
				</button>
			)}
		</div>
	)
}


function PlayCard({
					  id,
					  name,
					  formation,
					  playType,
					  defensiveFormation,
					  lastModified: _lastModified,
					  thumbnail,
					  drawings,
					  players,
					  personnel,
					  labels: _labels,
					  labelObjects,
					  selected = false,
					  onSelect,
					  onOpen,
					  onAnimate,
					  onRename,
					  onDelete,
					  onDuplicate,
					  cardType,
					  category,
					  description: _description,
					  animateIndex,
					  linkedFormationId,
					  linkedFormationName: _linkedFormationName,
					  onUnlink,
				  }: PlayCardProps) {
	return (
		<div
			className={`play-card ${selected ? 'play-card-selected' : ''}`.trim()}
			data-animate={animateIndex !== undefined ? 'reveal' : undefined}
			style={{
				...(animateIndex !== undefined && {
					'--reveal-delay': `${animateIndex * 60}ms`,
				}),
			} as React.CSSProperties}
		>
			<PlayCardThumbnail
				id={id}
				thumbnail={thumbnail}
				drawings={drawings}
				players={players}
				name={name}
				playType={playType}
				onOpen={() => onOpen(id)}
				onAnimate={onAnimate ? () => onAnimate(id) : undefined}
				cardType={cardType}
				category={category}
				isLinked={!!linkedFormationId}
			/>

			{onSelect && (
				<SelectButton
					selected={selected}
					onClick={() => onSelect(id)}
					className="play-card-select-button"
				/>
			)}

			<div className="play-card-content">
				<div className="play-card-header">
					<h3 className="play-card-title">{name}</h3>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button className="play-card-menu-button">
								<MoreVertical className="play-card-icon" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{onAnimate && (
								<DropdownMenuItem onClick={() => onAnimate(id)}>
									<Play className="play-card-icon" />
									Animate
								</DropdownMenuItem>
							)}
							<DropdownMenuItem onClick={() => onRename(id)}>
								<Edit className="play-card-icon" />
								Rename
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => onDuplicate(id)}>
								<Copy className="play-card-icon" />
								Duplicate
							</DropdownMenuItem>
							{linkedFormationId && onUnlink && (
								<DropdownMenuItem onClick={() => onUnlink(id)}>
									<Unlink className="play-card-icon" />
									Unlink Mirror
								</DropdownMenuItem>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => onDelete(id)} variant="destructive">
								<Trash2 className="play-card-icon" />
								Delete{linkedFormationId ? ' Both' : ''}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className="play-card-meta">
					{formation && (
						<span className="play-card-formation">
              {formation}
            </span>
					)}
					{personnel && (
						<>
							<span className="play-card-separator">•</span>
							<span className="play-card-personnel">
                {personnel}
              </span>
						</>
					)}
				</div>

				{defensiveFormation && (
					<p className="play-card-defensive-formation">
						vs {defensiveFormation}
					</p>
				)}

				<LabelChips labels={labelObjects ?? []} maxVisible={2} />
			</div>
		</div>
	)
}

// Memoize PlayCard to prevent unnecessary re-renders
export default memo(PlayCard, (prevProps, nextProps) => {
	// Return true if props are equal (skip re-render)
	// Compare all primitive props that affect rendering
	return (
		prevProps.id === nextProps.id &&
		prevProps.name === nextProps.name &&
		prevProps.formation === nextProps.formation &&
		prevProps.playType === nextProps.playType &&
		prevProps.defensiveFormation === nextProps.defensiveFormation &&
		prevProps.lastModified === nextProps.lastModified &&
		prevProps.thumbnail === nextProps.thumbnail &&
		prevProps.personnel === nextProps.personnel &&
		prevProps.selected === nextProps.selected &&
		prevProps.cardType === nextProps.cardType &&
		prevProps.category === nextProps.category &&
		prevProps.description === nextProps.description &&
		prevProps.animateIndex === nextProps.animateIndex &&
		prevProps.linkedFormationId === nextProps.linkedFormationId &&
		prevProps.linkedFormationName === nextProps.linkedFormationName &&
		// Compare arrays by content (not just reference) to detect coordinate changes
		drawingsEqual(prevProps.drawings, nextProps.drawings) &&
		playersEqual(prevProps.players, nextProps.players) &&
		prevProps.labelObjects?.length === nextProps.labelObjects?.length &&
		// Compare label objects by reference (if they haven't changed, array reference should be same)
		prevProps.labelObjects === nextProps.labelObjects
	)
})
